import { useMutation, useQueryClient } from "@tanstack/react-query";
import { privateAxios } from "../axios";
import { constructionSiteReceptionQueryKeys } from "../query-keys/constructionSiteReception.query-keys";
import { toast } from "sonner";
import { DocumentType, SuccessResponse } from "@dune/types/src";
import { constructionSiteSubmissionQueryKeys } from "../query-keys/constructionSiteSubmission.query-keys";
import { constructionSitesQueryKeys } from "../query-keys/constructionSites.query-keys";
import { constructionSiteAnalysisQueryKeys } from "../query-keys/constructionSiteAnalysis.query-keys";

export const useUpdateConstructionSiteReceptionDocuments = (
	constructionSiteId: number,
) => {
	const queryClient = useQueryClient();
	return useMutation({
		mutationFn: async (fileIds: string[]) => {
			const { data } = await privateAxios.patch(
				`/construction-sites-reception/${constructionSiteId}/documents`,
				{ fileIds },
			);
			return data;
		},
		onSuccess: () => {
			queryClient.invalidateQueries({
				queryKey: constructionSiteReceptionQueryKeys.folder(constructionSiteId),
			});
		},
		onError: (error) => {
			console.error(error);
			toast.error(
				"Une erreur est survenue lors de l'enregistrement des documents",
			);
		},
	});
};

const upsertEndorsementRenewalPricings = async (
	constructionSiteId: number,
	pricings: { binderWarrantyId: number }[],
) => {
	const { data } = await privateAxios.post(
		`/construction-sites-reception/${constructionSiteId}/endorsement-renewal-pricings`,
		{ pricings },
	);
	return data;
};

export const useUpsertEndorsementRenewalPricingsFromReception = () => {
	const queryClient = useQueryClient();

	return useMutation({
		mutationFn: ({
			constructionSiteId,
			pricings,
		}: {
			constructionSiteId: number;
			pricings: { binderWarrantyId: number }[];
		}) => upsertEndorsementRenewalPricings(constructionSiteId, pricings),
		onSuccess: (_data: SuccessResponse, { constructionSiteId }) => {
			queryClient.invalidateQueries({
				queryKey: ["construction-site-reception", constructionSiteId],
			});
		},
	});
};

export const linkFileToConstructionSiteReception = async (
	constructionSiteId: number,
	params: {
		fileId: string;
		type: DocumentType;
		participantId?: number;
		requiresLiftingReserves?: boolean;
	},
) => {
	const { data } = await privateAxios.post(
		`/construction-sites-reception/${constructionSiteId}/documents`,
		params,
	);
	return data;
};

export const useLinkFileToConstructionSiteReception = (
	constructionSiteId: number,
) => {
	const queryClient = useQueryClient();

	return useMutation({
		mutationFn: (params: {
			fileId: string;
			type: DocumentType;
			participantId?: number;
			requiresLiftingReserves?: boolean;
		}) => linkFileToConstructionSiteReception(constructionSiteId, params),
		onSuccess: () => {
			queryClient.invalidateQueries({
				queryKey:
					constructionSiteSubmissionQueryKeys.participantsReception(
						constructionSiteId,
					),
			});
			queryClient.invalidateQueries({
				queryKey:
					constructionSiteSubmissionQueryKeys.referenceableFiles(
						constructionSiteId,
					),
			});
			queryClient.invalidateQueries({
				queryKey: constructionSitesQueryKeys.documents(constructionSiteId),
			});
			queryClient.invalidateQueries({
				queryKey: [
					...constructionSiteAnalysisQueryKeys.analysisData(constructionSiteId),
					"reception-supporting-documents-analysis",
				],
			});
			queryClient.invalidateQueries({
				queryKey: [
					...constructionSiteSubmissionQueryKeys.detail(constructionSiteId),
					"reception",
				],
			});
			queryClient.invalidateQueries({
				queryKey: [
					...constructionSiteReceptionQueryKeys.detail(constructionSiteId),
					"operation-files",
				],
			});
		},
		onError: (error) => {
			console.error(error);
			toast.error("Erreur lors de la liaison du document");
		},
	});
};
